/**
 * @file login-form.component.ts
 * @description
 * This component handles the login functionality for the Superadmin Panel.
 * It allows superadmin users to authenticate and access the system securely.
 *
 * The component includes the following:
 * 1. A login form with input fields for:
 *    - Email (Required, Valid Email Format)
 *    - Password (Required)
 * 2. Form validation to ensure all required fields are properly filled.
 * 3. API integration to validate user credentials and authenticate login.
 * 4. Secure token management for maintaining user sessions.
 * 5. Error handling and user feedback (invalid credentials, network issues, etc.).
 * 6. Navigation to the dashboard upon successful login.
 * 7. Forgot Password link to reset credentials if needed.
 *
 * @dependencies:
 * - Authentication Service for handling login requests and token management.
 * - Angular Reactive Forms (`@angular/forms`) for form handling and validation.
 * - Angular Router (`@angular/router`) for navigation after successful login.
 * - Angular Material Snackbar (`MatSnackBar`) for displaying success and error messages.
 *
 * @param {MatSnackBar} snackBar - Used to display notifications for success and error messages.
 */

import { Component } from '@angular/core';
import { AbstractControl, FormGroup, ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SeoService } from '../../../../shared/services/seo.service';
import { localStorageConstants } from '../../../../consts/local_storage_constants';
import { encryptData, showNotification } from '../../../../consts/utils';
import { superAdminHttpRoutes, superAdminHttpVersion } from '../../../../consts/super_admin_http_routes';
import { ApiService } from '../../../../shared/services/api.services';
import { FormValidation } from '../../../../shared/services/utils/form-validation.service';
import { ForgotPasswordModalComponent } from '../../forgot-password/forgot-password.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SuperAdminUserActivityService } from '../../../../shared/services/superadminusers/superadmin-user-activity.service';
import { LoaderComponent } from '../../../../shared/components/loader/loader.component';


@Component({
  selector: 'app-login-form',
  imports: [ReactiveFormsModule,LoaderComponent],
  templateUrl: './login-form.component.html',
  styleUrls: ['./login-form.component.scss']
})

export class LoginFormComponent {

  public show: boolean = true;
  public isLoading: boolean = false; 

  form: FormGroup;
  public validate = false;

  constructor(
    private router: Router,
    private apiService: ApiService,
    private _modal: NgbModal,
    private _formBuilder: UntypedFormBuilder,
    private seo: SeoService,
    public formValidation: FormValidation,
    private snackBar: MatSnackBar,
    private userActivityService: SuperAdminUserActivityService
  ) {
    /**
           * Sets up SEO meta tags using SeoService
           * This helps in optimizing the page for search engines and social media previews.
       */
    this.seo.setTags({
      title: 'SuperAdmin Authentication Form', // Title
      titleSuffix: '- Shining On Safety', // Title Suffix
      description: 'Shining On Safety', // Description
      image: '', // Image
      keywords: 'Shining On Safety' // Keywords
    });
  }

  ngOnInit(): void {
    // Create the form
    this.form = this._formBuilder.group({
      email: ['',
        [
          Validators.compose([
            Validators.required,
            Validators.email
          ])
        ],
      ],
      password: ['',
        [
          Validators.compose([
            Validators.required,
            Validators.maxLength(50),
            Validators.minLength(6)
          ])
        ],
      ]
    });
  }
  /**
   *  Forgot password dialog 
   */
  openForgotPassword() {
    this._modal.open(ForgotPasswordModalComponent, { centered: true, backdrop: 'static', })
  }

  /**
   *  SignIn API call - encrypted email and password is passed into the body to login superadmin user
   */

  signIn() {
    this.validate = true;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.isLoading = true;
    const encryptedEmail = encryptData(this.form.value.email);
    const encryptedPassword = encryptData(this.form.value.password);

    const formData = {
      email: encryptedEmail,
      password: encryptedPassword
    };

    this.apiService.httpPostCallWithoutToken<any>(
      superAdminHttpVersion.V1 + superAdminHttpRoutes.LOGIN,
      formData
    ).subscribe({
      next: (response: any) => {
        this.isLoading = false; // Stop loading

        if (response.status) {
          showNotification(this.snackBar, response.message, 'success');
          this.userActivityService.trackUserActivity(response.data, true);

          localStorage.setItem(localStorageConstants.SUPER_ADMIN_TOKEN, response.data.token);
          localStorage.setItem(localStorageConstants.SUPER_ADMIN_DATA, JSON.stringify(response.data));
          localStorage.setItem(localStorageConstants.USER_TYPE, 'super-admin');
          sessionStorage.setItem(localStorageConstants.USER_TYPE, 'super-admin');
          localStorage.setItem(localStorageConstants.SUPER_ADMIN_LOGGED_IN, 'true');

          // Retrieve stored data and parse it
          const storedData = localStorage.getItem(localStorageConstants.SUPER_ADMIN_DATA);
          if (storedData) {
            //const parsedData = JSON.parse(storedData);
            this.router.navigate(['/superadmin/dashboard']);
          }
        } else {
          this.isLoading = false;
          showNotification(this.snackBar, response.message, 'error');
        }
      },
      error: (error: any) => {
        this.isLoading = false;
        showNotification(this.snackBar, error.message, 'error');
      }
    });
  }

  get f(): { [key: string]: AbstractControl } {
    return this.form.controls;
  }
}